import { motion } from 'framer-motion'

/**
 * Skeleton loader components for better perceived performance
 * Replaces generic "Loading..." text with content placeholders
 */

// Base skeleton bar
const SkeletonBar = ({ width = '100%', height = '1rem', className = '' }) => (
  <div
    className={`animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700 ${className}`}
    style={{ width, height }}
  />
)

// Skeleton card for dashboard stats
export const SkeletonCard = () => (
  <div className="soft-card p-6">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <SkeletonBar width="60%" height="0.875rem" className="mb-3" />
        <SkeletonBar width="40%" height="2rem" />
      </div>
      <div className="w-14 h-14 rounded-xl bg-gray-200 dark:bg-gray-700 animate-pulse" />
    </div>
  </div>
)

// Skeleton for table rows
export const SkeletonTableRow = ({ columns = 4 }) => (
  <tr className="border-b border-gray-200 dark:border-gray-700">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="p-4">
        <SkeletonBar width={i === 0 ? '80%' : '60%'} />
      </td>
    ))}
  </tr>
)

// Skeleton for list items
export const SkeletonListItem = () => (
  <div className="soft-card p-4 mb-3">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
      <div className="flex-1">
        <SkeletonBar width="70%" height="1rem" className="mb-2" />
        <SkeletonBar width="50%" height="0.875rem" />
      </div>
    </div>
  </div>
)

// Skeleton for form fields
export const SkeletonFormField = () => (
  <div className="space-y-2">
    <SkeletonBar width="30%" height="0.875rem" />
    <SkeletonBar width="100%" height="2.75rem" className="rounded-xl" />
  </div>
)

// Skeleton for page header
export const SkeletonPageHeader = () => (
  <div className="mb-8">
    <SkeletonBar width="40%" height="2rem" className="mb-3" />
    <SkeletonBar width="60%" height="1.25rem" />
  </div>
)

// Generic skeleton container
export const SkeletonContainer = ({ children, count = 1 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.1 }}
      >
        {children}
      </motion.div>
    ))}
  </div>
)

// Full page skeleton loader
export const SkeletonPage = () => (
  <div className="section-container">
    <SkeletonPageHeader />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
      <SkeletonContainer count={3}>
        <SkeletonCard />
      </SkeletonContainer>
    </div>
    <div className="soft-card p-6">
      <SkeletonBar width="30%" height="1.5rem" className="mb-4" />
      <SkeletonContainer count={5}>
        <SkeletonListItem />
      </SkeletonContainer>
    </div>
  </div>
)

